import './EntityTable.css'

/**
 * The table half of the card/table toggle, shared by the Quests, Locations,
 * NPCs and Items tabs.
 *
 * Each tab supplies a `columns` array; this component owns everything that
 * must behave identically across all four — row click target, hover and
 * focus states, the dimming of hidden rows, the actions column's click
 * isolation, and the collapse to stacked rows below 768px.
 *
 * Column descriptor:
 *
 *   {
 *     key:    string        // React key + a stable handle for the column
 *     header: string        // th text; '' for the lead/actions columns
 *     role:   'lead' | 'stripe' | 'name' | 'meta' | 'desc' | 'actions'
 *     render: (row) => node
 *   }
 *
 * `role` is the only sizing input a tab gives: 'name' and 'desc' are the
 * flexible columns, everything else is sized to its own content (see the
 * col-fit rule in EntityTable.css for why that matters). Tabs never pass
 * widths, so no tab can accidentally hand a two-value badge column a third
 * of the table.
 *
 * Fog of war needs no handling here. The API already filters hidden rows out
 * of a player's response, so `rows` can only ever contain what the caller was
 * allowed to receive — a table cannot leak what a card wouldn't have.
 */

const FLEXIBLE_ROLES = new Set(['name', 'desc'])

function columnClass(role) {
  // 'name' and 'desc' get their own sizing rules; every other role is
  // content-sized. The role also doubles as the mobile ordering hook.
  const base = FLEXIBLE_ROLES.has(role) ? `col-${role}` : `col-fit col-${role}`
  return base
}

export default function EntityTable({
  rows,
  columns,
  onRowClick,
  getRowKey = (row) => row.id,
  isDimmed = () => false,
  isStruck = () => false,
  emptyMessage = 'Nothing here yet.',
  label,
}) {
  const list = Array.isArray(rows) ? rows : []

  if (list.length === 0) {
    return <div className="entity-table-empty">{emptyMessage}</div>
  }

  return (
    <div className="entity-table-scroller">
      <table className="entity-table" aria-label={label}>
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key} className={columnClass(col.role)} scope="col">
                {col.header}
              </th>
            ))}
            {/* Pairs with the break cell in every body row so the column
                counts agree. Never rendered on either axis — see .col-break
                in EntityTable.css. */}
            <th className="col-break" aria-hidden="true"></th>
          </tr>
        </thead>
        <tbody>
          {list.map((row) => {
            const classes = [
              isDimmed(row) ? 'is-dimmed' : '',
              isStruck(row) ? 'is-struck' : '',
            ].filter(Boolean).join(' ')

            return (
              <tr
                key={getRowKey(row)}
                className={classes}
                onClick={() => onRowClick?.(row)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onRowClick?.(row)
                  }
                }}
                role="button"
                tabIndex={0}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={columnClass(col.role)}
                    // The actions cell swallows its own clicks so an edit or
                    // delete press never also opens the detail modal behind
                    // it. Done here rather than in each tab's render so no
                    // tab can forget it — the card grids each wrap
                    // AdminControls in their own stopPropagation div, and
                    // that is exactly the kind of thing that gets missed on
                    // the fifth copy.
                    onClick={col.role === 'actions' ? (e) => e.stopPropagation() : undefined}
                  >
                    {col.render(row)}
                  </td>
                ))}
                {/* A zero-height full-width flex item, used only by the
                    stacked mobile layout. A flex line is filled before its
                    items grow, so on a row with a short name the badges
                    would otherwise fit on the first line and land to the
                    right of the action buttons. This forces them below. */}
                <td className="col-break" aria-hidden="true"></td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
